import _get from 'lodash/get';
import _filter from 'lodash/filter';
import _cloneDeep from 'lodash/cloneDeep';
import _isEmpty from 'lodash/isEmpty';
import _pick from 'lodash/pick';
import _noop from 'lodash/noop';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import ReactTimeout from 'react-timeout';
import {
  selectGradingType,
  selectRoundPercentType,
} from '../../../selectors/settings';
import { getGradebook } from '../../../actions/getGradebook';
import { updateAlert, updateStudent } from '../../../actions/gradebook';
import {
  unlinkReefStudent,
  isUserLinked,
  handleAlertReef,
} from '../../../actions/reef';
import { selectGradebookCategories } from '../../../selectors/categories';
import { selectGradebookCourse } from '../../../selectors/gradebook';
import StudentGradebook from '../StudentGradebook/StudentGradebook';
import useModalStates from '../../modals/useModalStates';
import StatusIndicator from '../../StatusIndicator/StatusIndicator';
import { LinkReefDialog } from '../../modals/LinkReefDialog/LinkReefDialog';
import GradebookCommentDialog from '../../modals/GradebookCommentDialog/GradebookCommentDialog';
import StudentIDs from '../../modals/StudentIDs/StudentIDs';

const getFilteredGradebookProps = (gradebook = {}) => {
  const { filterValue } = gradebook;
  const filteredGradebook = _cloneDeep(gradebook);

  const filteredParticipants = filteredGradebook.students.filter(
    (student) =>
      _get(
        student,
        'name',
        `${student.first_name} ${student.last_name}`,
      ).includes(filterValue.toLowerCase()) ||
      student.email.toLowerCase().includes(filterValue.toLowerCase()),
  );
  filteredGradebook.students = filteredParticipants;

  return {
    filteredGradebook,
    filteredParticipants: filteredParticipants.length,
    participants: gradebook.students.length,
  };
};

function StudentGradebookContainer({
  getGradebook,
  gradebook,
  assignments,
  user,
  categories,
  categoryWeights,
  course,
  courseId,
  currentStudent,
  gradingType,
  roundPercentType,
  isGradebookFetching,
  isGradebookReFetching,
  alert,
  updateAlert,
  handleAlertReef,
  updateStudent,
  unlinkReefStudent,
  isReefLinked,
  isReefStatusFetching,
  rdxGradebookSettings,
  isUserLinked,
  getLaunchUrl = _noop,
  handleLocalLaunch = _noop,
  settings,
  disableEnforceFocus,
}) {
  const {
    linkReefDialog,
    showLinkReefDialog,
    showGradebookCommentDialog,
    gradebookCommentDialog,
    showStudentIDModal,
    studentID,
    dispatch,
  } = useModalStates();

  useEffect(() => {
    getGradebook(courseId);
    isUserLinked(courseId);
  }, [courseId, getGradebook, isUserLinked]);

  const filteredGradebookProps = getFilteredGradebookProps(gradebook);
  const noAlertVisible = _isEmpty(alert) || !alert.show;
  return (
    <>
      {showLinkReefDialog && (
        <LinkReefDialog
          onClose={() => linkReefDialog.close()}
          dispatch={dispatch}
          userIsInstructor={false}
          userIsStudent
          courseId={courseId}
          isUserReefLinked={_get(user, 'isReefLinked')}
          handleAlertReef={handleAlertReef}
          hasInstructorLinkedCourse={gradebook.hasInstructorLinkedCourse}
          disableEnforceFocus={disableEnforceFocus}
        />
      )}
      {showGradebookCommentDialog && (
        <GradebookCommentDialog
          onClose={() => gradebookCommentDialog.close()}
          show={showGradebookCommentDialog}
          gradeCommentData={gradebookCommentDialog.data}
          getLaunchUrl={getLaunchUrl}
          handleLocalLaunch={handleLocalLaunch}
          disableEnforceFocus={disableEnforceFocus}
        />
      )}

      <StudentIDs
        isOpen={showStudentIDModal}
        isEditingStudentId={studentID.isEditing}
        handleClose={studentID.close}
        gradebookSettings={settings}
        handleSubmit={(value) => studentID.submit(value, user)}
        disableEnforceFocus={disableEnforceFocus}
      />

      {noAlertVisible && (
        <StatusIndicator
          isReefLinked={isReefLinked}
          hasInstructorLinkedCourse={gradebook.hasInstructorLinkedCourse}
          isStudent
          gracePeriodExpiresIn={0}
          isReefStatusFetching={isReefStatusFetching}
          onClickButton={linkReefDialog.toggle}
        />
      )}
      <StudentGradebook
        studentGradebook={_get(
          filteredGradebookProps,
          'filteredGradebook.students[0]',
          {},
        )}
        assignments={assignments}
        user={user}
        categories={categories}
        categoryWeights={categoryWeights}
        currentStudent={currentStudent}
        gradingType={gradingType}
        roundPercentType={roundPercentType}
        isGradebookFetching={isGradebookFetching}
        isGradebookReFetching={isGradebookReFetching}
        showGradebookCommentDialog={gradebookCommentDialog.open}
        showEditStudentId={() => studentID.open(true)} // send true to show the editing option
        alert={alert}
        updateAlert={updateAlert}
        updateStudent={updateStudent}
        rdxGradebookSettings={rdxGradebookSettings}
        unlinkReefStudent={unlinkReefStudent}
        course={course}
        courseId={courseId}
        getLaunchUrl={getLaunchUrl}
        handleLocalLaunch={handleLocalLaunch}
        settings={settings}
      />
    </>
  );
}

function mapStateToProps(state) {
  const requiredIClickerProperties = [
    'isReefLinked',
    'isReefStatusFetching',
    'reefProfile',
    'reefCourseName',
    'thirdPartyUserId',
  ];

  const userIClickerProperties = _pick(
    _get(state, 'user', {}),
    requiredIClickerProperties,
  );

  const { settings } = state.gradebook;
  return {
    settings,
    gradebook: state.gradebook,
    gradingType: selectGradingType(state),
    roundPercentType: selectRoundPercentType(state),
    alert: state.gradebook.alert,
    categories: selectGradebookCategories(state),
    course: selectGradebookCourse(state),
    currentStudent: state.gradebook.currentStudent,
    categoryWeights: state.gradebook.categoryWeights,
    user: {
      ...state.gradebook.user,
      ...userIClickerProperties,
    },
    permissions: state.gradebook.permissions,
    rdxGradebookSettings: _get(state, 'gradebook.settings', []),
    isGradebookFetching: state.gradebook.isGradebookFetching,
    isGradebookReFetching: state.gradebook.isGradebookReFetching,
    assignments: _filter(
      state.gradebook.assignments,
      (assignment) => assignment.assignment_due_at !== null,
    ),
    isReefLinked: _get(state, 'user.isReefLinked', false),
    isReefStatusFetching: _get(state, 'user.isReefStatusFetching', false),
  };
}

const mapDispatchToProps = {
  getGradebook,
  handleAlertReef,
  updateAlert,
  updateStudent,
  unlinkReefStudent,
  isUserLinked,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ReactTimeout(StudentGradebookContainer));
